import { useEffect, useState } from 'react';
import { IconButton, SxProps, Theme, Tooltip } from '@mui/material';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import { useTranslation } from 'react-i18next';
import { useAlert } from '@components/showAlert/useAlertHook';
import {
    NOTIFICATIONS_CHANGED, notificationsEnabled, notificationsSupported, setNotificationsEnabled,
} from 'functions/browser-notifications';

interface Props {
    sx?: SxProps<Theme>;
}

/// Liga/desliga os avisos do navegador; a permissão só pode ser pedida a partir de um clique.
export const NotificationsToggle = ({ sx }: Props) => {
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const [enabled, setEnabled] = useState(notificationsEnabled);

    useEffect(() => {
        const refresh = () => setEnabled(notificationsEnabled());

        window.addEventListener(NOTIFICATIONS_CHANGED, refresh);
        return () => window.removeEventListener(NOTIFICATIONS_CHANGED, refresh);
    }, []);

    if (!notificationsSupported()) return null;

    const toggle = async () => {
        if (enabled) {
            setNotificationsEnabled(false);
            return;
        }

        const permission = Notification.permission === 'default'
            ? await Notification.requestPermission()
            : Notification.permission;

        // Recusado uma vez, o navegador não pergunta de novo: só desbloqueia no cadeado do endereço.
        if (permission !== 'granted') {
            showAlert(t('notificacoes.bloqueadas'), 'warning', 6000);
            return;
        }

        setNotificationsEnabled(true);
        showAlert(t('notificacoes.ligadas'), 'success', 3000);
    };

    return (
        <Tooltip title={enabled ? t('notificacoes.desligar') : t('notificacoes.ligar')}>
            <IconButton size="small" color="inherit" onClick={toggle} sx={sx}>
                {enabled ? <NotificationsActiveOutlinedIcon /> : <NotificationsOffOutlinedIcon />}
            </IconButton>
        </Tooltip>
    );
};
